import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { PackageOpen, Plus, X, Pencil, Trash2, Download, FileSpreadsheet, Truck, Send, ChevronDown, Search, ShieldAlert } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const STATUS_COLORS = {
  draft: "bg-slate-500/15 text-slate-500",
  sent: "bg-blue-500/15 text-blue-600",
  received: "bg-emerald-500/15 text-emerald-600",
  cancelled: "bg-rose-500/15 text-rose-600",
};

const today = () => new Date().toISOString().slice(0, 10);

export default function VendorDailyOrder() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [date, setDate] = useState(today());
  const [vendor, setVendor] = useState("");
  const [status, setStatus] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (date) params.set("date", date);
      if (vendor) params.set("vendor", vendor);
      if (status) params.set("status", status);
      const r = await api.get(`/vendor-orders${params.toString() ? `?${params}` : ""}`);
      setOrders(r.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [date, status]);

  const totals = useMemo(() => ({
    orders: orders.length,
    items: orders.reduce((n, o) => n + (o.item_count || 0), 0),
    amount: orders.reduce((n, o) => n + (o.total_amount || 0), 0),
  }), [orders]);

  const downloadWithAuth = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const filteredIds = orders.map((o) => o.id);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) filteredIds.forEach((id) => n.delete(id));
    else filteredIds.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} order(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/vendor-orders/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  if (user?.role === "company_admin") {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-24 text-center">
        <ShieldAlert className="h-12 w-12 text-destructive" />
        <h1 data-testid="vendor-orders-access-denied" className="mt-4 font-heading text-2xl font-bold">Access Denied</h1>
        <p className="mt-2 text-sm text-muted-foreground">Supplier Daily Orders are not available for Company Admin accounts.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">Procurement</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Supplier Daily Order</h1>
          <p className="mt-1 text-sm text-muted-foreground">Track daily purchase orders to suppliers and export to Excel.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            data-testid="vdo-export-daily"
            disabled={!date}
            onClick={() => downloadWithAuth(`/vendor-orders/export.xlsx?date=${date}${vendor ? `&vendor=${encodeURIComponent(vendor)}` : ""}`, `vendor-orders-${date}.xlsx`)}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-60"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-500" /> Export day
          </button>
          <button
            data-testid="vdo-create-open"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-3.5 w-3.5" /> New order
          </button>
        </div>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <KPI label="Orders" value={totals.orders} icon={<Truck className="h-4 w-4" />} />
        <KPI label="Items" value={totals.items} icon={<PackageOpen className="h-4 w-4" />} />
        <KPI label="Total value (₹)" value={totals.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })} icon={<Send className="h-4 w-4" />} />
      </div>

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="label-eyebrow mb-1 block">Date</label>
          <div className="flex items-center gap-1">
            <input
              data-testid="vdo-date-filter"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <button
              data-testid="vdo-list-all"
              type="button"
              onClick={() => setDate("")}
              className={`rounded-md border px-3 py-2 text-xs font-semibold ${!date ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}
              title="Show orders from all dates"
            >
              All dates
            </button>
          </div>
        </div>
        <div className="relative flex-1 max-w-xs">
          <label className="label-eyebrow mb-1 block">Vendor</label>
          <Search className="pointer-events-none absolute left-3 top-8 h-4 w-4 text-muted-foreground" />
          <input
            data-testid="vdo-vendor-filter"
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Filter by vendor…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow mb-1 block">Status</label>
          <select data-testid="vdo-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-input bg-background px-3 py-2 text-sm">
            <option value="">Any</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        <button onClick={load} className="rounded-md border border-border/70 px-3 py-2 text-xs font-medium">Apply</button>
      </div>

      <div className="sharp-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-secondary/30 text-left">
              <tr>
                <th className="w-10 px-4 py-2">
                  <RowCheckbox testid="vdo-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
                </th>
                <th className="px-4 py-2 label-eyebrow">Order #</th>
                <th className="px-4 py-2 label-eyebrow">Date</th>
                <th className="px-4 py-2 label-eyebrow">Vendor</th>
                <th className="px-4 py-2 label-eyebrow">Items</th>
                <th className="px-4 py-2 label-eyebrow">Total (₹)</th>
                <th className="px-4 py-2 label-eyebrow">Status</th>
                <th className="px-4 py-2 label-eyebrow">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`vdo-row-${o.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                  <td className="px-4 py-2">
                    <RowCheckbox testid={`vdo-select-${o.id}`} checked={selected.has(o.id)} onChange={() => toggleOne(o.id)} />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold text-primary">{o.order_no}</td>
                  <td className="px-4 py-2 text-muted-foreground">{o.order_date}</td>
                  <td className="px-4 py-2">
                    <p className="font-medium">{o.vendor_name}</p>
                    <p className="text-[11px] text-muted-foreground">{o.vendor_email || o.vendor_phone || "—"}</p>
                  </td>
                  <td className="px-4 py-2 font-semibold">{o.item_count}</td>
                  <td className="px-4 py-2 font-semibold">₹{(o.total_amount || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_COLORS[o.status] || STATUS_COLORS.draft}`}>{o.status}</span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button
                        data-testid={`vdo-download-${o.id}`}
                        title="Download Excel"
                        onClick={() => downloadWithAuth(`/vendor-orders/${o.id}/export.xlsx`, `${o.order_no}.xlsx`)}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`vdo-edit-${o.id}`}
                        onClick={() => setEditing(o)}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`vdo-delete-${o.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete ${o.order_no}?`)) return;
                          try { await api.delete(`/vendor-orders/${o.id}`); await load(); } catch (e) { setErr(formatApiError(e)); }
                        }}
                        className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-xs text-muted-foreground">{date ? `No orders on ${date}.` : "No orders yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <BulkBar count={selected.size} entityLabel="orders" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <OrderModal defaultDate={date} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <OrderModal existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function KPI({ label, value, icon }) {
  return (
    <div className="sharp-card p-4">
      <div className="flex items-center justify-between">
        <p className="label-eyebrow">{label}</p>
        <span className="text-muted-foreground">{icon}</span>
      </div>
      <p className="mt-1 font-heading text-2xl font-bold">{value}</p>
    </div>
  );
}

function OrderModal({ existing, defaultDate, onClose, onSaved }) {
  const [form, setForm] = useState({
    order_date: existing?.order_date || defaultDate || today(),
    vendor_name: existing?.vendor_name || "",
    vendor_email: existing?.vendor_email || "",
    vendor_phone: existing?.vendor_phone || "",
    notes: existing?.notes || "",
    status: existing?.status || "draft",
  });
  const [items, setItems] = useState(existing?.items?.length ? existing.items.map((it) => ({ ...it })) : [{ part_name: "", part_number: "", brand: "", quantity: 1, unit_price: 0, remarks: "" }]);
  const [spares, setSpares] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/spares").then((r) => setSpares(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateItem = (i, patch) => setItems((rows) => rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setItems([...items, { part_name: "", part_number: "", brand: "", quantity: 1, unit_price: 0, remarks: "" }]);
  const removeRow = (i) => setItems(items.length > 1 ? items.filter((_, idx) => idx !== i) : items);

  const pickSpare = (i, spareId) => {
    if (!spareId) return updateItem(i, { spare_id: null, part_name: "", part_number: "", brand: "" });
    const s = spares.find((x) => x.id === spareId);
    if (!s) return;
    updateItem(i, {
      spare_id: s.id,
      part_name: s.part_name,
      part_number: s.part_number || "",
      brand: s.brand || "",
    });
  };

  const total = items.reduce((n, it) => n + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const cleanItems = items
        .filter((it) => (it.part_name || "").trim())
        .map((it) => ({
          spare_id: it.spare_id || null,
          part_name: (it.part_name || "").trim(),
          part_number: (it.part_number || "").trim() || null,
          brand: (it.brand || "").trim() || null,
          quantity: Math.max(1, Number(it.quantity) || 1),
          unit_price: Number(it.unit_price) || 0,
          remarks: (it.remarks || "").trim() || null,
        }));
      if (cleanItems.length === 0) throw new Error("Add at least one item with a part name.");
      const payload = { ...form, items: cleanItems };
      if (existing) await api.patch(`/vendor-orders/${existing.id}`, payload);
      else await api.post("/vendor-orders", payload);
      onSaved();
    } catch (e) { setErr(e.response ? formatApiError(e) : e.message); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl sharp-card max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border/60 bg-card px-5 py-3">
          <div>
            <p className="label-eyebrow text-primary">{existing ? existing.order_no : "New vendor order"}</p>
            <h3 className="mt-1 font-heading text-lg font-semibold">{existing ? `Edit — ${existing.vendor_name}` : "Create vendor order"}</h3>
          </div>
          <button data-testid="vdo-modal-close" onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <div>
              <label className="label-eyebrow mb-1.5 block">Order date *</label>
              <input data-testid="vdo-form-date" type="date" required value={form.order_date} onChange={set("order_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Vendor name *</label>
              <input data-testid="vdo-form-vendor" required value={form.vendor_name} onChange={set("vendor_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Status</label>
              <select data-testid="vdo-form-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="received">Received</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Vendor email</label>
              <input data-testid="vdo-form-email" type="email" value={form.vendor_email} onChange={set("vendor_email")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="label-eyebrow mb-1.5 block">Vendor phone</label>
              <input data-testid="vdo-form-phone" value={form.vendor_phone} onChange={set("vendor_phone")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="label-eyebrow">Items</p>
              <button type="button" data-testid="vdo-item-add" onClick={addRow} className="inline-flex items-center gap-1 rounded-md border border-border/70 px-2 py-1 text-[11px] font-semibold hover:bg-secondary">
                <Plus className="h-3 w-3" /> Add row
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} data-testid={`vdo-item-row-${i}`} className="rounded-md border border-border/60 p-3">
                  <div className="grid gap-2 sm:grid-cols-12">
                    <div className="sm:col-span-3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">From Spare Master (optional)</label>
                      <select
                        data-testid={`vdo-item-spare-${i}`}
                        value={it.spare_id || ""}
                        onChange={(e) => pickSpare(i, e.target.value)}
                        className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
                      >
                        <option value="">— Manual —</option>
                        {spares.map((s) => <option key={s.id} value={s.id}>{s.part_id} · {s.part_name}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-3">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Part name *</label>
                      <input data-testid={`vdo-item-name-${i}`} required value={it.part_name} onChange={(e) => updateItem(i, { part_name: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Part No</label>
                      <input data-testid={`vdo-item-partno-${i}`} value={it.part_number || ""} onChange={(e) => updateItem(i, { part_number: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Qty</label>
                      <input data-testid={`vdo-item-qty-${i}`} type="number" min="1" value={it.quantity} onChange={(e) => updateItem(i, { quantity: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Unit ₹</label>
                      <input data-testid={`vdo-item-price-${i}`} type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1 flex items-end">
                      <button type="button" data-testid={`vdo-item-remove-${i}`} onClick={() => removeRow(i)} className="w-full rounded-md border border-border/70 px-2 py-1.5 text-muted-foreground hover:text-destructive">
                        <Trash2 className="mx-auto h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="sm:col-span-11">
                      <input placeholder="Remarks (optional)" value={it.remarks || ""} onChange={(e) => updateItem(i, { remarks: e.target.value })} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs" />
                    </div>
                    <div className="sm:col-span-1 flex items-center justify-end text-[11px] font-mono text-muted-foreground">
                      ₹{((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end text-sm">
              <span className="text-muted-foreground">Total:&nbsp;</span>
              <span data-testid="vdo-form-total" className="font-heading font-bold">₹{total.toFixed(2)}</span>
            </div>
          </div>

          <div>
            <label className="label-eyebrow mb-1.5 block">Notes</label>
            <textarea data-testid="vdo-form-notes" rows={2} value={form.notes} onChange={set("notes")} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>

          {err && <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="vdo-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Create order")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
